import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { useCurrency } from '../context/CurrencyContext';
import { F, MONO_FEAT, withAlpha } from '../styles/theme';
import { fmtC } from '../utils/format';
import { getNoBasisTxIds, getOpenTransferTxs, getExternalReceiveTxs } from '../utils/dataCheck';
import CoinImage from '../components/CoinImage';
import BackButton from '../components/BackButton';

// ── „Zu klären" — Fokus-Liste der problematischen TX ─────────────────────────
// Gemeinsames Ziel BEIDER Wege (Datencheck-Sheet + Wallet-„Offene Transfers"):
// listet die TX, die Klärung brauchen — ohne Gegenpart (offene Transfers) UND
// ohne Kaufpreis (echte Käufe/Swaps). Quellen identisch zu Wallet/Datencheck
// (isOpenTransfer / getNoBasisTxIds). Jede Zeile öffnet den Bearbeiten-Flow
// (onEdit → TransactionEditScreen: dort Gegenpart verbinden ODER Preis ergänzen).

const txKey = (tx) => tx._docId || (tx.id != null ? String(tx.id) : undefined);

function fmtDate(d, locale) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function FixRow({ T, tx, marketData, locale, onEdit }) {
  const sym = tx.coin || tx.coinIn || tx.sym || '';
  const menge = Number(tx.menge ?? tx.mengeIn) || 0;
  return (
    <button
      type="button"
      className="mo-row"
      onClick={() => onEdit?.(tx)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: '15px 6px', margin: '0 -6px',
        background: 'transparent', border: 'none',
        borderBottom: `1px solid ${T.lineSoft || T.line}`,
        cursor: 'pointer', textAlign: 'left', color: T.text,
        WebkitTapHighlightColor: 'transparent', transition: 'background .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = withAlpha(T.text, 0.04); }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ flexShrink: 0, display: 'inline-flex' }}><CoinImage sym={sym} marketData={marketData} size={34} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em' }}>{sym}</span>
        <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, display: 'block', fontSize: 12, color: T.sub, marginTop: 2 }}>
          {fmtC(menge)} {sym} · {fmtDate(tx.datum, locale)}{tx.exchange ? ` · ${tx.exchange}` : ''}
        </span>
      </span>
      <span className="mo-chev" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', color: T.sub }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </button>
  );
}

function Section({ T, label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 2px' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.warn || '#B8862F', flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.mute }}>{label}</span>
      <span style={{ ...MONO_FEAT, fontFamily: F.num || F.mono, fontSize: 11, color: T.faint || T.mute, fontWeight: 700 }}>{count}</span>
      <span style={{ flex: 1, height: 1, background: T.lineSoft || T.line }} />
    </div>
  );
}

export default function DataCheckFixScreen({ txs = [], marketData = {}, onBack, onEdit }) {
  const { T } = useTheme();
  const { t } = useT();
  const { locale } = useCurrency();

  const openTransfers = useMemo(() => getOpenTransferTxs(txs, marketData), [txs, marketData]);
  const externalReceives = useMemo(() => getExternalReceiveTxs(txs, marketData), [txs, marketData]);
  const noBasis = useMemo(() => {
    const ids = getNoBasisTxIds(txs);
    return (txs || []).filter((tx) => ids.has(txKey(tx)));
  }, [txs]);

  const sortByDateDesc = (a, b) => (a.datum > b.datum ? -1 : a.datum < b.datum ? 1 : 0);
  const transfersSorted = [...openTransfers].sort(sortByDateDesc);
  const externalSorted = [...externalReceives].sort(sortByDateDesc);
  const basisSorted = [...noBasis].sort(sortByDateDesc);
  const total = transfersSorted.length + externalSorted.length + basisSorted.length;

  return (
    <div style={{ padding: '8px 0 100px', color: T.text, fontFamily: F.family, maxWidth: 720, margin: '0 auto' }}>
      {/* Kopf */}
      <div style={{ padding: '8px 18px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <BackButton onBack={onBack} title={t('back')} size={36} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.sub, marginBottom: 4 }}>{t('dc_kicker')}</div>
          <h1 style={{ fontFamily: F.display, fontSize: 28, fontWeight: 800, letterSpacing: '-0.035em', margin: 0, lineHeight: 1.04 }}>
            {t('dc_fix_title')}
          </h1>
        </div>
      </div>

      <div style={{ padding: '0 18px 16px', borderBottom: `1px solid ${T.line}` }}>
        <p style={{ margin: 0, fontSize: 14, color: T.sub, lineHeight: 1.5, maxWidth: 560 }}>
          {total > 0 ? t('dc_fix_sub', { n: total }) : t('dc_fix_empty')}
        </p>
      </div>

      <div style={{ padding: '0 18px' }}>
        {/* Ohne Gegenpart (offene Transfers) */}
        {transfersSorted.length > 0 && (
          <>
            <Section T={T} label={t('dc_fix_sect_transfer')} count={transfersSorted.length} />
            {transfersSorted.map((tx) => (
              <FixRow key={txKey(tx)} T={T} tx={tx} marketData={marketData} locale={locale} onEdit={onEdit} />
            ))}
          </>
        )}

        {/* Extern empfangen (kein Gegenpart, echter Coin) — Quelle zuordnen */}
        {externalSorted.length > 0 && (
          <>
            <Section T={T} label={t('dc_fix_sect_external')} count={externalSorted.length} />
            {externalSorted.map((tx) => (
              <FixRow key={txKey(tx)} T={T} tx={tx} marketData={marketData} locale={locale} onEdit={onEdit} />
            ))}
          </>
        )}

        {/* Ohne Kaufpreis (echte Käufe/Swaps) */}
        {basisSorted.length > 0 && (
          <>
            <Section T={T} label={t('dc_fix_sect_basis')} count={basisSorted.length} />
            {basisSorted.map((tx) => (
              <FixRow key={txKey(tx)} T={T} tx={tx} marketData={marketData} locale={locale} onEdit={onEdit} />
            ))}
          </>
        )}
      </div>

    </div>
  );
}
